import React, {
  ReactElement,
  FocusEvent,
  MouseEvent,
  KeyboardEvent,
  CSSProperties,
  Children,
  useState,
  useRef,
  useEffect
} from 'react';
import isNil from 'lodash.isnil';
import uniqueId from 'lodash.uniqueid';
import makeClass from 'clsx';
import { ThemeableElement, useTheme } from '@design-systems/theme';
import { Menu, MenuItemProps } from '@ids-ts/menu';
import TextField from '@ids-ts/text-field';
import { ChevronDownIcon } from '@ids-ts/icon';
import { PreventOverflow } from '@ids-ts/position';
import { checkForA11yProps } from '@ids-ts/utils';
import styles from './Dropdown.css';
import {
  ReactDropdownEvent,
  ReactEventRelatedTarget,
  searchType,
  OnEventInfoType,
  selectionType,
  ReactFocusEventRelatedTarget,
  Alignments,
  Positions
} from './types';

export interface DropdownProps
  extends Omit<ThemeableElement<'div'>, 'onChange' | 'onBlur'> {
  /** ID for automation test. */
  automationId?: string;
  /** Children of Menu, which are MenuItem types in the dropdown */
  children?: ReactElement<MenuItemProps> | ReactElement<MenuItemProps>[];
  /**  Disable dropdown if true. */
  disabled?: boolean;
  /**
   * Filter option for dropdown menu items
   *
   * @default false
   */
  enableFilterItems?: boolean;
  /**
   * Flag to set if onBlur should be called on multiSelect.
   * Added to ensure we do not break existing users.
   *
   * @default false
   */
  enableOnBlurOnMultiselect?: boolean;
  /**  Error message to be rendered. */
  errorText?: string;
  /** Boolean flag to allow label to be bold */
  hasWeightedLabel?: boolean;
  /**  Placeholder string for the search filter.
   *
   * @default 'Filter'
   */
  filterPlaceholder?: string;
  /** helper message to be rendered. */
  helperText?: string;
  /**  Id for component. */
  id?: string;
  /** Label to be displayed for the field. */
  label?: string;
  /** Class to be applied to menu. */
  menuClass?: string;
  /** Enable Multi Select dropdown if true. */
  multiselect?: boolean;
  /** Name to be assigned to the input field. */
  name?: string;
  /**  Callback function to be executed on blur event. */
  onBlur?: (e: FocusEvent, infoObject?: OnEventInfoType) => void;
  /** Callback function to be executed on change, receives 2 params: event object and value. */
  onChange?: (
    e: KeyboardEvent | MouseEvent,
    infoObject?: OnEventInfoType
  ) => void;
  /**
   * Callback function to handle filter, also performs callback with value { type: 'clickAway' }
   * when clicked away from the filter input field
   */
  onItemsFilter?: (arg?: Record<'type', string>) => void;
  /** Callback function to be executed on dropdown open event. */
  onOpen?: () => void;
  /**
   * preventMenuOverflow: an object variable; used to prevent Dropdown Menu from being positioned outside the boundary.
   * More detail in @ids-ts/position preventOverflow API document.
   */
  preventMenuOverflow?: PreventOverflow;
  /** Display value directly from props, do not memoize it */
  renderFromProps?: boolean;
  /**
   * Determine which search type to apply while typing
   * full - search sequentially adding typed symbol from the beginning
   * first - mimic all behavior to match first char only
   * partial - search for the first occurrence in the middle of displayed items
   *
   * @default 'full'
   */
  searchMode?: searchType;
  /** change text field color to warn user without text and icon */
  validationError?: boolean;
  /** Selected value. */
  value?: string | number | (string | number)[];
  /** warning message to be rendered. */
  warningText?: string;
  /** The optional styles applied to the position node wrapped around the root node. */
  stylePosition?: CSSProperties;
  /**
   * vertical positions: top, bottom
   * horizontal positions: left, right
   * NOTE: horizontal alignments cannot be paired with horizontal positions
   *
   * @default 'bottom'
   */
  position?: Positions;
  /**
   * vertical alignments: top, bottom, middle
   * horizontal alignments: left, right, center
   * NOTE: horizontal alignments cannot be paired with horizontal positions
   *
   * @default 'right'
   */
  alignment?: Alignments;
  /**
   * offsetSkidding: the cross-axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   */
  offsetSkidding?: number;
  /**
   * offsetDistance: the main-axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   */
  offsetDistance?: number;
  /**
   * width: width of the dropdown
   *
   * @default 'fit-content'
   */
  width?: string;
}

/**
 * Compare two arrays
 *
 * @param a - array one
 * @param b - array two
 * @returns true if match
 */
const compareArrays = (a: selectionType, b: selectionType) => {
  if (a.length !== b.length || !Array.isArray(a) || !Array.isArray(b))
    return false;
  return (
    a.filter((c) => b.includes(c)).length === a.length &&
    b.filter((c) => a.includes(c)).length === b.length
  );
};

/**
 * Return label for dropdown menu item
 * - @param child Menu Item children
 */
const getLabel = (child: MenuItemProps) => {
  const tempLabel =
    (child && child.menuItemLabel) ||
    (child && child.children) ||
    (child && child.value);
  return tempLabel;
};

/**
 * @param selectedValue - value
 * @param children - properties object
 * @returns with label and value fields
 */
const getMenuItem = (selectedValue: string | any[], children: any) => {
  let label: string[] = [];
  let value: string[] = [];
  Children.forEach(children, (child) => {
    if (Array.isArray(selectedValue)) {
      if (
        selectedValue.includes(child && child.props.value) ||
        selectedValue.includes(child && child.props.children)
      ) {
        child && label.push(getLabel(child.props) as string);
        child && value.push(child.props.value as string);
      }
    } else if (
      selectedValue === (child && child.props.value) ||
      selectedValue === (child && child.props.children)
    ) {
      child && label.push(getLabel(child.props) as string);
      child && value.push(child.props.value as string);
    }
  });

  if (label.length === 0 || value.length === 0) {
    label = [''];
    value = [''];
  }

  return { label, value };
};

const prefix = 'idsDropdown-Menu-';

/**
 * A themeable dropdown component.
 * Any supplied props that are not defined here will be provided to the root element.
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const Dropdown = (props: DropdownProps) => {
  const {
    alignment = 'right',
    'aria-label': ariaLabel,
    automationId,
    children,
    className,
    colorScheme,
    disabled,
    enableFilterItems = false,
    enableOnBlurOnMultiselect = false,
    errorText,
    hasWeightedLabel,
    filterPlaceholder = 'Filter',
    id,
    label,
    menuClass,
    multiselect,
    name,
    offsetDistance = 0,
    offsetSkidding = 0,
    onBlur,
    onChange,
    onItemsFilter,
    onOpen,
    position = 'bottom',
    preventMenuOverflow,
    renderFromProps,
    searchMode = 'full',
    style,
    stylePosition,
    theme,
    validationError,
    value,
    warningText,
    width,
    ...html
  } = props;

  checkForA11yProps('Dropdown', props, [
    'label',
    'aria-label',
    'aria-labelledby'
  ]);

  const [autoFocus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [defaultValue, setDefaultValue] = useState(value);
  const [selectedItemValue, setSelectedItemValue] = useState<any[]>(
    Array.isArray(value) ? [...value] : [value]
  );
  const [searchEvent, setSearchEvent] = useState<string>('');
  const { current: ariaId } = useRef(uniqueId(prefix));
  const menuListRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chevronRef = useRef<HTMLInputElement | null>(null);
  const rootElement = useRef<HTMLDivElement | null>(null);

  // a11y in FF 40 to 46 fix.
  const skipBlur = useRef(false);
  const skipClose = useRef(false);
  const [parentWidth, setParentWidth] = useState(0);
  const { current: menuFilterTextField } = useRef(
    uniqueId('menuFilterTextField')
  );
  const componentClassName = 'idsTSDropdown';
  const { current: ariaIdTextField } = useRef(uniqueId('idsDropdownTextField'));

  useEffect(() => {
    isNil(value)
      ? setSelectedItemValue([])
      : setSelectedItemValue(Array.isArray(value) ? [...value] : [value]);

    if (rootElement && rootElement.current) {
      setParentWidth(rootElement.current.offsetWidth);
    }
  }, [value]);

  useEffect(() => {
    if (Array.isArray(value) && Array.isArray(defaultValue)) {
      if (!compareArrays(defaultValue, value)) {
        setDefaultValue(Array.isArray(value) ? [...value] : [value]);
        setSelectedItemValue(Array.isArray(value) ? [...value] : [value]);
      }
    }

    if (defaultValue !== value) {
      setDefaultValue(value);
      isNil(value) ? setSelectedItemValue([]) : setSelectedItemValue([value]);
    }
  }, [defaultValue, value]);

  /**
   * Get Active Element
   * - @param e React Event
   */
  const getActiveElement = (e: FocusEvent) =>
    e.relatedTarget || document.activeElement;

  /**
   * Handle Close event
   * - @param e Mouse or Keyboard Event
   */
  const handleClose = (e: MouseEvent | KeyboardEvent | FocusEvent) => {
    const activeElement = getActiveElement(e as FocusEvent);

    if (enableFilterItems) {
      onItemsFilter && onItemsFilter({ type: 'clickAway' });
    }

    if (
      menuListRef.current &&
      menuListRef.current.contains(activeElement as HTMLElement)
    ) {
      skipClose.current = false;
    }

    // ignore this use-case, as it is only a failsafe and cannot be tested
    /* istanbul ignore next */
    if (skipClose.current) {
      skipClose.current = false;
      return;
    }

    setOpen(false);
    setSearchEvent('');
    inputRef.current && inputRef.current.focus();
    skipClose.current = false;
  };

  /** handleMenuOnBlurWithMultiSelect */
  const handleMenuOnBlurWithMultiselect = (e: KeyboardEvent | FocusEvent) => {
    onBlur && onBlur(e as any);
    skipBlur.current = true;
  };

  /**
   * Handle Click Away
   * - @param e Keyboard or Mouse event
   */
  const handleClickAway = (e: ReactDropdownEvent) => {
    if (
      e &&
      (menuListRef.current?.contains(e.target as HTMLElement) ||
        inputRef.current?.contains(e.target as HTMLElement) ||
        chevronRef.current?.contains(e.target as HTMLElement))
    ) {
      return;
    }

    if (enableFilterItems) {
      onItemsFilter && onItemsFilter({ type: 'clickAway' });
    }

    if (open) {
      skipBlur.current = false;
      setOpen(false);
      setSearchEvent('');
      onBlur && onBlur(e as any);
    }
  };

  /** Handle TextField Blur */
  const handleTextFieldBlur = (e: FocusEvent) => {
    e.preventDefault();
    const activeElement = getActiveElement(e) as HTMLElement;
    if (
      !enableFilterItems &&
      !skipBlur.current &&
      open &&
      !e.target.contains(activeElement) &&
      menuListRef.current &&
      !menuListRef.current.contains(activeElement)
    ) {
      setOpen(false);
      setSearchEvent('');
      if (name) {
        /* istanbul ignore next */
        onBlur &&
          onBlur({
            ...e,
            target: { ...e.target, name }
          } as ReactFocusEventRelatedTarget);
      } else {
        onBlur && onBlur(e);
      }
    } else if (!skipBlur.current && !open) {
      if (name) {
        /* istanbul ignore next */
        onBlur &&
          onBlur({
            ...e,
            target: { ...e.target, name }
          } as ReactFocusEventRelatedTarget);
      } else {
        onBlur && onBlur(e);
      }
    }
  };

  /**
   * Handle Keyboard events
   * - @param e Keyboard Events
   */
  const handleKeyDown = (e: KeyboardEvent) => {
    // Autofill key is 'Unidentified'.
    if (e.key === 'Unidentified') {
      return;
    }

    if (['ArrowDown', ' ', 'Enter'].includes(e.key)) {
      e.preventDefault();
      skipClose.current = true;
      setOpen(true);
      setFocus(true);
      onOpen && onOpen();
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      handleClose(e);
      inputRef.current && inputRef.current.focus();
    } else if (e.key === 'Tab' && open) {
      e.preventDefault();
      handleClose(e);
      inputRef.current && inputRef.current.focus();
    } else if (open) {
      e.preventDefault();
      skipClose.current = true;
      setSearchEvent(e.key);
    } else if (e.key.length === 1) {
      e.preventDefault();
      skipClose.current = true;
      setOpen(true);
      setFocus(true);
      onOpen && onOpen();
      setSearchEvent(e.key);
    }
  };

  /**
   * Handle menu item selection
   * - @param e React Form Event
   */
  const handleSelect = (e: MouseEvent | KeyboardEvent) => {
    // ensure not to update selected items if filter input value is changed
    if (e && (e.target as HTMLElement).id === menuFilterTextField) {
      return;
    }

    const element = e.target as HTMLFormElement;
    const nextSelected = getMenuItem(element.value, children);
    if (!multiselect) {
      if (!compareArrays(selectedItemValue, nextSelected.value)) {
        if (name) {
          /* istanbul ignore next */
          onChange &&
            onChange({
              ...e,
              target: { ...e.target, name }
            } as ReactEventRelatedTarget);
        } else {
          onChange && onChange(e);
        }
      }
    } else if (name) {
      /* istanbul ignore next */
      onChange &&
        onChange({
          ...e,
          target: { ...e.target, name }
        } as ReactEventRelatedTarget);
    } else {
      onChange && onChange(e);
    }

    setSelectedItemValue(nextSelected.value);

    if (!multiselect) {
      setOpen(false);
      setSearchEvent('');
      handleClose(e);
      inputRef.current && inputRef.current.focus();
    }
  };

  /**
   * assign ref to Menu List
   * - @param setRefNode refer to menu list node
   */
  const assignMenuListRef = (c: HTMLDivElement | null) => {
    menuListRef.current = c;
  };

  /** Handle on Click */
  const handleClick = () => {
    const nextOpenState = !open;
    if (nextOpenState) {
      onOpen && onOpen();
    }

    setOpen(nextOpenState);
    setSearchEvent('');
    setFocus(false);
  };

  /** Selected Item */
  let selectedItem: {
    /** value */
    value: string | string[];
    /** label */
    label: selectionType;
  };
  if ((multiselect || renderFromProps) && value) {
    selectedItem = getMenuItem(value as string, children);
  } else {
    selectedItem = getMenuItem(selectedItemValue, children);
  }

  const { currentTheme, themeClass, currentColorScheme } = useTheme(
    { theme, colorScheme },
    styles
  );
  if (
    rootElement.current &&
    rootElement.current.offsetWidth &&
    parentWidth !== rootElement.current.offsetWidth
  ) {
    setParentWidth(rootElement.current.offsetWidth);
  }

  /** Handle Mouse Down Event */
  const handleIconMouseDown = (e: MouseEvent) => e.preventDefault();

  /** Render Chevron Down Icon */
  const getIcon = () => (
    <div
      ref={chevronRef}
      aria-hidden
      className={makeClass(styles.iconBox, themeClass, {
        [styles.iconBoxDisabled]: disabled
      })}
      role="button"
      tabIndex={-1}
      onMouseDown={handleIconMouseDown}
    >
      <ChevronDownIcon
        className={makeClass(styles.chevronDownIcon, {
          [styles.chevronDownIconError]: errorText || validationError,
          [styles.chevronDownIconWarning]: !errorText && warningText
        })}
        size="medium"
      />
    </div>
  );

  /** Render TextField Element */
  const renderInputElement = () => (
    <TextField
      ref={inputRef}
      autoComplete="off"
      {...html}
      readOnly
      disableReadOnlyStyle
      role="combobox"
      aria-controls={id ? `${prefix}${id}` : ariaId}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-label={ariaLabel}
      label={label}
      className={makeClass(currentTheme, themeClass, {
        [styles.textField]: !disabled
      })}
      theme={currentTheme}
      id={id || ariaIdTextField}
      errorText={errorText}
      warningText={warningText}
      validationError={validationError}
      value={selectedItem.label.join(', ')}
      width={width}
      disabled={disabled}
      hasWeightedLabel={hasWeightedLabel}
      colorScheme={currentColorScheme}
      addonAfter={getIcon()}
      onKeyDown={disabled ? undefined : handleKeyDown}
      onClick={disabled ? undefined : handleClick}
      onBlur={handleTextFieldBlur}
    />
  );

  /** Render TextField Element */
  const renderItems = () => {
    const items = Array.isArray(children) ? [...children] : children;
    if (enableFilterItems && Array.isArray(items)) {
      const filter = (
        <div className={styles.filter}>
          <TextField
            autoFocus
            aria-label={filterPlaceholder}
            placeholder={filterPlaceholder}
            id={menuFilterTextField}
            onChange={onItemsFilter}
          />
        </div>
      );
      items.unshift(filter);
    }

    return items;
  };

  return (
    <div
      ref={rootElement}
      className={makeClass(
        componentClassName,
        className,
        styles.dropdownContainer
      )}
      style={style}
      data-automation-id={automationId}
    >
      <Menu
        showCheckmark
        scrollToSelected
        accessStringMatch={searchEvent}
        alignment={alignment}
        anchorElement={renderInputElement()}
        anchorElementRefPropName="assignLabelRef"
        aria-labelledby={id || ariaIdTextField}
        autoFocus={!enableFilterItems && autoFocus}
        className={makeClass(menuClass, themeClass, styles.menu)}
        colorScheme={currentColorScheme}
        disableSearch={enableFilterItems}
        id={id ? `${prefix}${id}` : ariaId}
        ignoreIdsOnBlur={[menuFilterTextField]}
        maxHeight="300px"
        menuItemRole="option"
        menuOffsetDistance={offsetDistance}
        menuOffsetSkidding={offsetSkidding}
        menuRole="listbox"
        minWidth={`${parentWidth || 200}px`}
        open={open}
        position={position}
        preventMenuOverflow={preventMenuOverflow}
        reference={assignMenuListRef}
        searchMode={searchMode}
        selectedItem={selectedItem.value}
        style={style}
        stylePosition={stylePosition}
        theme={currentTheme}
        onBlur={
          multiselect && enableOnBlurOnMultiselect
            ? handleMenuOnBlurWithMultiselect
            : undefined
        }
        onClickAway={handleClickAway}
        onClose={handleClose}
        onSelect={handleSelect}
      >
        {renderItems()}
      </Menu>
    </div>
  );
};

export default Dropdown;
